import moment from 'moment/moment'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import _ from 'lodash'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Popover } from 'reactstrap'
import { categoriesFilterOptions } from '../../../reducers/dashboardReducer'
import DatePicker from '../../common/form/DatePicker'
import Select from '../../common/form/Select'
import SubmitButton from '../../common/buttons/SubmitButton'

import styles from './FiltersButtonDropdown.module.scss'
import { ReactComponent as FilterIcon } from '../../../assets/images/common/icons/filter-icon.svg'

const useWidgetIds = (prefix) => useMemo(() => {
  const base = _.uniqueId(`${prefix}-`)
  return {
    buttonId: `${base}-button`,
    startDateId: `${base}-start-date`,
    endDateId: `${base}-end-date`,
    categoryId: `${base}-category`,
    programId: `${base}-program`,
  }
}, [prefix])

const currentDate = new Date()

export const FIXED_DATE_RANGES_FUNCTIONS = {
  '90days': {
    textFunc: () => ({ tKey: 'dashboard.coach.filtersButtonDropdown.fixedRange.last90Days' }),
    valueFunc: () => ({
      endDate: currentDate,
      startDate: moment().subtract(90, 'days')
        .toDate(),
    }),
  },
  '60days': {
    textFunc: () => ({ tKey: 'dashboard.coach.filtersButtonDropdown.fixedRange.last60Days' }),
    valueFunc: () => ({
      endDate: currentDate,
      startDate: moment().subtract(60, 'days')
        .toDate(),
    }),
  },
  '30days': {
    textFunc: () => ({ tKey: 'dashboard.coach.filtersButtonDropdown.fixedRange.last30Days' }),
    valueFunc: () => ({
      endDate: currentDate,
      startDate: moment().subtract(30, 'days')
        .toDate(),
    }),
  },
  '20days': {
    textFunc: () => ({ tKey: 'dashboard.coach.filtersButtonDropdown.fixedRange.last20Days' }),
    valueFunc: () => ({
      endDate: currentDate,
      startDate: moment().subtract(20, 'days')
        .toDate(),
    }),
  },
  thisYear: {
    textFunc: () => ({ tKey: 'dashboard.coach.filtersButtonDropdown.fixedRange.thisYear', values: { year: moment().year() } }),
    valueFunc: () => ({
      startDate: moment().startOf('year')
        .toDate(),
      endDate: moment().endOf('year')
        .toDate(),
    }),
  },
  lastYear: {
    textFunc: () => ({ tKey: 'dashboard.coach.filtersButtonDropdown.fixedRange.lastYear', values: { year: moment().subtract(1, 'year').year() } }),
    valueFunc: () => ({
      startDate: moment().subtract(1, 'year').startOf('year')
        .toDate(),
      endDate: moment().subtract(1, 'year').endOf('year')
        .toDate(),
    }),
  },
}

const getTitleText = (startDate, endDate) => {
  const startDateMoment = moment(startDate)
  const endDateMoment = moment(endDate)
  const startThisYear = moment(currentDate).startOf('year')
  const endThisYear = moment(currentDate).endOf('year')
  const startLastYear = moment(currentDate).subtract(1, 'year').startOf('year')
  const endLastYear = moment(currentDate).subtract(1, 'year').endOf('year')

  if (startDateMoment.isSame(startThisYear, 'day') && endDateMoment.isSame(endThisYear, 'day')) {
    return { tKey: 'dashboard.coach.filtersButtonDropdown.title.thisYear', values: { year: currentDate.getFullYear() } }
  }

  if (startDateMoment.isSame(startLastYear, 'day') && endDateMoment.isSame(endLastYear, 'day')) {
    return { tKey: 'dashboard.coach.filtersButtonDropdown.title.lastYear', values: { year: currentDate.getFullYear() - 1 } }
  }

  if (endDateMoment.isSame(moment(currentDate), 'day')) {
    const ranges = {
      20: 'dashboard.coach.filtersButtonDropdown.title.last20Days',
      30: 'dashboard.coach.filtersButtonDropdown.title.last30Days',
      31: 'dashboard.coach.filtersButtonDropdown.title.last30Days',
      60: 'dashboard.coach.filtersButtonDropdown.title.last60Days',
      90: 'dashboard.coach.filtersButtonDropdown.title.last90Days',
    }
    const diffDays = endDateMoment.diff(startDateMoment, 'days') + 1
    if (ranges[diffDays]) {
      return { tKey: ranges[diffDays] }
    }
  }

  return {
    tKey: 'dashboard.coach.filtersButtonDropdown.title.dateRange',
    values: { startDate: startDate.toLocaleDateString(), endDate: endDate.toLocaleDateString() },
  }
}

const FixedRangeList = ({ onSelectRange, className, buttonClassName }) => {
  const { t } = useTranslation()

  return (
    <ul className={className}>
      {Object.keys(FIXED_DATE_RANGES_FUNCTIONS).map(key => {
        const { tKey, values } = FIXED_DATE_RANGES_FUNCTIONS[key].textFunc()

        return (
          <li key={key}>
            <button className={buttonClassName} onClick={() => onSelectRange(key)}>
              {t(tKey, values)}
            </button>
          </li>
        )
      })}
    </ul>
  )
}

FixedRangeList.propTypes = {
  onSelectRange: PropTypes.func.isRequired,
  className: PropTypes.string,
  buttonClassName: PropTypes.string,
}

const FiltersButtonDropdown = ({ title, defaultFilters, appliedFilters, onSubmitFilters, dateOnly }) => {
  const { t } = useTranslation()
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [editFilters, setEditFilters] = useState()

  const ids = useWidgetIds()

  const categoriesSelectOptions = useSelector(categoriesFilterOptions)
  const { programsFilterOptions } = useSelector(state => state.dashboard)

  const programsSelectOptions = editFilters?.categoryOption
    ? programsFilterOptions.filter(program => program.categoryId === editFilters.categoryOption.value)
    : programsFilterOptions

  const extraFiltersCount = dateOnly
    ? 0
    : (appliedFilters.categoryOption !== null ? 1 : 0) + (appliedFilters.programOption !== null ? 1 : 0)

  const titleText = getTitleText(appliedFilters.startDate, appliedFilters.endDate)

  const handleToggle = () => {
    if (!isPopoverOpen) {
      setEditFilters(appliedFilters)
    }
    setIsPopoverOpen(prev => !prev)
  }

  const handleSetFixedRange = (range) => {
    const { startDate, endDate } = FIXED_DATE_RANGES_FUNCTIONS[range].valueFunc()
    setEditFilters(prev => ({ ...prev, startDate, endDate }))
  }

  const handleSetFilterValue = (filterName, value) => {
    setEditFilters(prev => ({ ...prev, [filterName]: value }))
  }

  const handleSetCategoryFilterValue = (categoryOption) => {
    const program = editFilters.programOption

    if (program !== null && categoryOption !== null && program.categoryId !== categoryOption.value) {
      setEditFilters(prev => ({ ...prev, programOption: null }))
    }

    setEditFilters(prev => ({ ...prev, categoryOption }))
  }

  const handleClear = () => {
    onSubmitFilters(defaultFilters)
    setIsPopoverOpen(false)
  }

  const handleSubmit = () => {
    onSubmitFilters(editFilters)
    setIsPopoverOpen(false)
  }

  return (
    <>
      <button
        id={ids.buttonId}
        className={styles.filtersButton}
        onClick={handleToggle}
      >
        <FilterIcon className={styles.filterIcon} />
        <span>{t(titleText.tKey, titleText.values)}</span>
        {extraFiltersCount > 0 && (
          <span className={styles.extraFilter}>{t('dashboard.coach.filtersButtonDropdown.extraFiltersCount', { count: extraFiltersCount })}</span>
        )}
      </button>

      <Popover
        target={ids.buttonId}
        isOpen={isPopoverOpen}
        placement='bottom-end'
        trigger='legacy'
        toggle={handleToggle}
        popperClassName={classnames(styles.filtersMenu, { [styles.dateOnlyMenu]: dateOnly })}
      >
        <div className={classnames(styles.filtersBody, { [styles.dateOnlyBody]: dateOnly })}>
          {!dateOnly && (
            <div className={styles.fixedRangeSidebar}>
              <span className={styles.sidebarTitle}>{t('dashboard.coach.filtersButtonDropdown.fixedRangeTitle')}</span>
              <FixedRangeList
                onSelectRange={handleSetFixedRange}
                className={styles.rangeList}
                buttonClassName={styles.rangeButton}
              />
            </div>
          )}

          <div className={styles.filtersPanel}>
            <h5 className={styles.panelTitle}>{title ?? t('dashboard.coach.filtersButtonDropdown.filterOptionsTitle')}</h5>

            <div className={styles.datePickersRow}>
              <div className={styles.datePickerField}>
                <label className={styles.fieldLabel} htmlFor={ids.startDateId}>{t('common.field.startDate')}</label>
                <DatePicker
                  id={ids.startDateId}
                  className={styles.datePicker}
                  selected={editFilters?.startDate}
                  onChange={date => handleSetFilterValue('startDate', date)}
                  dateFormat='MMMM, d eee'
                  symbol={<i className={classnames(styles.calendarIcon, 'fa fa-calendar')} />}
                />
              </div>
              <div className={styles.datePickerField}>
                <label className={styles.fieldLabel} htmlFor={ids.endDateId}>{t('common.field.endDate')}</label>
                <DatePicker
                  id={ids.endDateId}
                  className={styles.datePicker}
                  selected={editFilters?.endDate}
                  onChange={date => handleSetFilterValue('endDate', date)}
                  dateFormat='MMMM, d eee'
                  symbol={<i className={classnames(styles.calendarIcon, 'fa fa-calendar')} />}
                />
              </div>
            </div>

            {dateOnly && (
              <FixedRangeList
                onSelectRange={handleSetFixedRange}
                className={styles.inlineRangeList}
                buttonClassName={styles.inlineRangeButton}
              />
            )}

            {!dateOnly && (
              <>
                <div className={styles.selectField}>
                  <label className={styles.fieldLabel} htmlFor={ids.categoryId}>
                    {t('dashboard.coach.filtersButtonDropdown.programCategoryLabel')}
                  </label>
                  <Select
                    id={ids.categoryId}
                    placeholder={t('dashboard.coach.filtersButtonDropdown.programCategoryPlaceholder')}
                    options={categoriesSelectOptions}
                    value={editFilters?.categoryOption}
                    classNames={{ menuList: () => styles.selectMenuList }}
                    onChange={option => handleSetCategoryFilterValue(option)}
                    isClearable
                  />
                </div>
                <div className={classnames(styles.selectField, styles.selectFieldSpaced)}>
                  <label className={styles.fieldLabel} htmlFor={ids.programId}>{t('dashboard.coach.filtersButtonDropdown.programTitleLabel')}</label>
                  <Select
                    id={ids.programId}
                    isSearchable
                    placeholder={t('dashboard.coach.filtersButtonDropdown.programTitlePlaceholder')}
                    options={programsSelectOptions}
                    value={editFilters?.programOption}
                    classNames={{ menuList: () => styles.selectMenuList }}
                    onChange={option => handleSetFilterValue('programOption', option)}
                    isClearable
                  />
                </div>
              </>
            )}

            <div className={styles.actionButtons}>
              <button className={styles.clearButton} onClick={handleClear}>
                {t('common.action.clear')} <i className='ml-4 fa fa-close' />
              </button>
              <SubmitButton
                buttonMessage={t('common.action.apply')}
                className={styles.applyButton}
                onClick={handleSubmit}
              />
            </div>
          </div>
        </div>
      </Popover>
    </>
  )
}

FiltersButtonDropdown.propTypes = {
  title: PropTypes.string,
  defaultFilters: PropTypes.shape({
    startDate: PropTypes.instanceOf(Date).isRequired,
    endDate: PropTypes.instanceOf(Date).isRequired,
    categoryOption: PropTypes.object,
    programOption: PropTypes.object,
  }).isRequired,
  appliedFilters: PropTypes.shape({
    startDate: PropTypes.instanceOf(Date).isRequired,
    endDate: PropTypes.instanceOf(Date).isRequired,
    categoryOption: PropTypes.object,
    programOption: PropTypes.object,
  }).isRequired,
  onSubmitFilters: PropTypes.func.isRequired,
  dateOnly: PropTypes.bool,
}

export default FiltersButtonDropdown
